/**
 * usePushNotifications Hook
 *
 * Manages push notification state and provides methods to
 * enable/disable push notifications for the current device.
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/auth';
import {
  isPushSupported,
  getPermissionState,
  registerServiceWorker,
  isSubscribed as checkIsSubscribed,
  enablePushNotifications,
  disablePushNotifications,
  listDevices,
  removeDevice,
  type PushDevice,
  type PushPermissionState,
} from '@/services/pushService';

// Storage key for tracking if user has dismissed the opt-in prompt
const DISMISSED_KEY = 'push-opt-in-dismissed';
const DISMISSED_UNTIL_KEY = 'push-opt-in-dismissed-until';

// How long to wait before showing the prompt again after dismissal (7 days)
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;

export interface UsePushNotificationsResult {
  // State
  isSupported: boolean;
  permission: PushPermissionState;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  devices: PushDevice[];

  // Prompt management
  shouldShowPrompt: boolean;
  dismissPrompt: (permanently?: boolean) => void;

  // Actions
  enable: (deviceName?: string) => Promise<boolean>;
  disable: () => Promise<boolean>;
  refreshDevices: () => Promise<void>;
  removeDeviceById: (deviceId: string) => Promise<boolean>;
}

export function usePushNotifications(): UsePushNotificationsResult {
  const { isAuthenticated } = useAuth();

  const [isSupported] = useState(() => isPushSupported());
  const [permission, setPermission] = useState<PushPermissionState>(() =>
    getPermissionState()
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [devices, setDevices] = useState<PushDevice[]>([]);
  const [promptDismissed, setPromptDismissed] = useState(() => {
    // Check if permanently dismissed
    if (localStorage.getItem(DISMISSED_KEY) === 'true') {
      return true;
    }
    // Check if temporarily dismissed
    const dismissedUntil = localStorage.getItem(DISMISSED_UNTIL_KEY);
    if (dismissedUntil) {
      return Date.now() < parseInt(dismissedUntil, 10);
    }
    return false;
  });

  // Determine if we should show the opt-in prompt
  const shouldShowPrompt =
    isAuthenticated &&
    isSupported &&
    permission === 'default' &&
    !isSubscribed &&
    !isLoading &&
    !promptDismissed;

  // Initialize: register service worker and check subscription state
  useEffect(() => {
    async function init() {
      if (!isSupported) {
        setIsLoading(false);
        return;
      }

      try {
        // Register service worker
        await registerServiceWorker();

        // Check if currently subscribed
        const subscribed = await checkIsSubscribed();
        setIsSubscribed(subscribed);

        // Update permission state
        setPermission(getPermissionState());
      } catch (err) {
        console.error('Failed to initialize push notifications:', err);
      } finally {
        setIsLoading(false);
      }
    }

    init();
  }, [isSupported]);

  // Fetch devices when authenticated and subscribed
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      refreshDevices();
    }
  }, [isAuthenticated, isLoading]);

  // Refresh the list of registered devices
  const refreshDevices = useCallback(async () => {
    if (!isAuthenticated) {
      setDevices([]);
      return;
    }

    try {
      const deviceList = await listDevices();
      setDevices(deviceList);
    } catch (err) {
      console.error('Failed to fetch devices:', err);
      // Don't set error for device fetch failures
    }
  }, [isAuthenticated]);

  // Dismiss the opt-in prompt
  const dismissPrompt = useCallback((permanently = false) => {
    if (permanently) {
      localStorage.setItem(DISMISSED_KEY, 'true');
    } else {
      localStorage.setItem(
        DISMISSED_UNTIL_KEY,
        (Date.now() + DISMISS_DURATION_MS).toString()
      );
    }
    setPromptDismissed(true);
  }, []);

  // Enable push notifications
  const enable = useCallback(
    async (deviceName?: string): Promise<boolean> => {
      if (!isSupported) {
        setError('Push notifications are not supported');
        return false;
      }

      setIsLoading(true);
      setError(null);

      try {
        await enablePushNotifications(deviceName);
        setIsSubscribed(true);
        setPermission(getPermissionState());
        await refreshDevices();
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to enable push notifications';
        setError(message);
        setPermission(getPermissionState());
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [isSupported, refreshDevices]
  );

  // Disable push notifications
  const disable = useCallback(async (): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      await disablePushNotifications();
      setIsSubscribed(false);
      await refreshDevices();
      return true;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to disable push notifications';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [refreshDevices]);

  // Remove a specific device
  const removeDeviceById = useCallback(
    async (deviceId: string): Promise<boolean> => {
      setError(null);

      try {
        await removeDevice(deviceId);
        await refreshDevices();
        // Check if current device was removed
        const stillSubscribed = await checkIsSubscribed();
        setIsSubscribed(stillSubscribed);
        return true;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to remove device';
        setError(message);
        return false;
      }
    },
    [refreshDevices]
  );

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    devices,
    shouldShowPrompt,
    dismissPrompt,
    enable,
    disable,
    refreshDevices,
    removeDeviceById,
  };
}

